import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useSmartAndRegularAssets from "../../../api/hooks/useSmartAndRegularAssets";
import { entityKeys } from "../../../api/queryKeys";
import InitialScanBanner from "../../../components/InitialScanBanner";
import LoadingIcon from "../../../components/LoadingIcon";
import AppPage from "../../../components/structure/AppPage";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import { InventoryBootstrapProvider } from "../../../contexts/InventoryBootstrapContext";
import { ComplexEntityTypes, QueryParam } from "../../../utils/constants";
import EntityOverviewView from "./entityOverviewPage/EntityOverviewView";
import EntityTypeView from "./entityTypePage/EntityTypeView";
import { useEntities } from "./hooks/useEntities";

export const overviewType = {
  CARD: "card",
  TABLE: "table",
};

export default function EnvironmentView() {
  const { getEntitiesOverview } = useAPI();
  const [searchParams] = useSearchParams();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);

  const entitiesOverviewQuery = useQuery({
    queryKey: entityKeys.entitiesOverview(),
    queryFn: getEntitiesOverview,
    select: (response) => response.data,
  });

  const { useQueryForAllEntities } = useEntities();
  const useQueryForAllEntitiesResult = useQueryForAllEntities(
    entitiesOverviewQuery.isSuccess && !isActiveRds
  );

  const {
    areAllSuccess: assetsFetchedSuccessfully,
    regularAndActiveSmartAsset,
    isError: assetsHaveError,
  } = useSmartAndRegularAssets(false);

  let entityType = null;
  let entitiesOfCurrentType = [];
  let entityTypeQueryParam = searchParams.get(QueryParam.environmentType);

  if (Object.values(ComplexEntityTypes).includes(entityTypeQueryParam)) {
    entityType = entityTypeQueryParam;
    if (!isActiveRds && useQueryForAllEntitiesResult.isSuccess) {
      entitiesOfCurrentType = useQueryForAllEntitiesResult.data.filter(
        (entity) => entity.entity_type === entityType
      );
    }
  }

  const isOverviewPageReady =
    entitiesOverviewQuery.isSuccess &&
    (assetsFetchedSuccessfully || isActiveRds) &&
    !entityType;

  const isEntityTypePageReady =
    entityType &&
    entitiesOverviewQuery.isSuccess &&
    (isActiveRds || useQueryForAllEntitiesResult.isSuccess);

  return (
    <InventoryBootstrapProvider>
      <AppPage title="Inventory">
        <InitialScanBanner />
        {isEntityTypePageReady ? (
          <EntityTypeView
            key={entityType}
            entityType={entityType}
            fetchedEntities={entitiesOfCurrentType}
            entitiesAreFetching={useQueryForAllEntitiesResult.isFetching}
            entitiesOverviewData={entitiesOverviewQuery.data}
            assets={regularAndActiveSmartAsset}
          />
        ) : isOverviewPageReady ? (
          <EntityOverviewView
            useQueryForAllEntities={useQueryForAllEntitiesResult}
            entitiesOverviewData={entitiesOverviewQuery.data}
            assets={regularAndActiveSmartAsset}
          />
        ) : assetsHaveError ||
          entitiesOverviewQuery.isError ||
          useQueryForAllEntitiesResult.isError ? (
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ paddingTop: "20px" }}
          >
            This page is currently unavailable
          </Typography>
        ) : (
          <Box paddingTop="40px">
            <LoadingIcon />
          </Box>
        )}
      </AppPage>
    </InventoryBootstrapProvider>
  );
}
